import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { User as UserIcon, ShieldCheck, Building2, Users, Camera, Loader2, ArrowLeft } from "lucide-react";

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const { lang } = useLang();
  const nav = useNavigate();
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", bio: "", business_name: "", photo_url: "",
  });

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        bio: user.bio || "",
        business_name: user.business_name || "",
        photo_url: user.photo_url || "",
      });
    }
  }, [user]);

  if (loading) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: "/profile" }} replace />;

  const partnerRole = user.partner_role;
  const isPartner = partnerRole === "broker" || partnerRole === "builder";

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // /upload/photo returns { path: "..." } — turn into full URL
      const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const url = data.path?.startsWith("http") ? data.path : `${REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success(lang === "hi" ? "फ़ोटो अपलोड हुई" : "Photo uploaded");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      toast.success(lang === "hi" ? "प्रोफ़ाइल सेव हुई!" : "Profile saved!");
      await refresh?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <button onClick={() => nav(-1)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
        <ArrowLeft size={12} /> {lang === "hi" ? "वापस" : "Back"}
      </button>

      <div className="mb-6">
        <div className="overline text-secondary">{lang === "hi" ? "आपकी प्रोफ़ाइल" : "Your Profile"}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">
          {lang === "hi" ? "अपनी जानकारी अपडेट करें" : "Update Your Info"}
        </h1>
        {isPartner && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-secondary/15 text-secondary border border-secondary/40" data-testid="partner-role-badge">
            {partnerRole === "builder" ? <Building2 size={12} /> : <Users size={12} />}
            {partnerRole === "builder" ? "Builder" : "Broker"}
            {user.kyc_status === "verified" && <ShieldCheck size={12} className="ml-1 text-emerald-700" />}
          </div>
        )}
      </div>

      <form onSubmit={save} className="space-y-5">
        <Card className="p-5">
          <div className="flex items-start gap-4">
            <div className="relative">
              {form.photo_url ? (
                <img src={form.photo_url} alt="you" className="w-24 h-24 rounded-full object-cover border-2 border-border" data-testid="profile-photo-preview" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-muted grid place-items-center text-3xl font-bold text-muted-foreground">
                  {(form.name || form.business_name || "?")[0]?.toUpperCase()}
                </div>
              )}
              <label
                htmlFor="photo-upload"
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground grid place-items-center cursor-pointer shadow-md hover:opacity-90"
                data-testid="upload-photo-label"
              >
                {uploadingPhoto ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
              </label>
              <input id="photo-upload" type="file" accept="image/*" hidden onChange={uploadPhoto} disabled={uploadingPhoto} data-testid="photo-upload-input" />
            </div>
            <div className="flex-1">
              <div className="text-xs text-muted-foreground mb-1">{lang === "hi" ? "प्रोफ़ाइल फ़ोटो" : "Profile photo"}</div>
              <div className="text-sm font-semibold">
                {lang === "hi" ? "अपनी असली फ़ोटो अपलोड करें" : "Upload a clear headshot"}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {isPartner
                  ? (lang === "hi" ? "आपकी फ़ोटो हर लिस्टिंग के साथ दिखेगी — भरोसा बढ़ता है।" : "Your photo shows on every listing — builds buyer trust.")
                  : (lang === "hi" ? "प्रोफ़ाइल पर आपकी पहचान बनाए।" : "Sets your identity across the app.")}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div>
            <Label htmlFor="name">{lang === "hi" ? "पूरा नाम" : "Full name"}</Label>
            <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
          </div>
          <div>
            <Label htmlFor="phone">{lang === "hi" ? "फ़ोन नंबर" : "Phone"}</Label>
            <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="profile-phone" placeholder="+91 98765 43210" />
          </div>

          {isPartner && (
            <>
              <div>
                <Label htmlFor="biz">{partnerRole === "builder" ? (lang === "hi" ? "बिल्डर / फ़र्म का नाम" : "Builder / Firm name") : (lang === "hi" ? "एजेंसी का नाम" : "Agency name")}</Label>
                <Input id="biz" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="profile-business" placeholder={partnerRole === "builder" ? "e.g. Aashirwad Constructions" : "e.g. Marwar Realty"} />
              </div>
              <div>
                <Label htmlFor="bio">{lang === "hi" ? "बायो / परिचय" : "Bio / About"}</Label>
                <Textarea id="bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="profile-bio" placeholder={lang === "hi" ? "अपने अनुभव, विशेषज्ञता के बारे में लिखें…" : "Share your experience, specialisation, projects…"} className="min-h-[100px]" />
              </div>
            </>
          )}
        </Card>

        <div className="flex justify-end">
          <Button type="submit" size="lg" className="bg-primary hover:bg-primary/90 rounded-full px-8" disabled={saving} data-testid="profile-save">
            {saving && <Loader2 className="animate-spin mr-2" size={14} />}
            {lang === "hi" ? "सेव करें" : "Save profile"}
          </Button>
        </div>
      </form>
    </div>
  );
}
